/**
 * Credits.
 *
 * Credits are the product's unit of billing, so this screen has to answer three
 * questions without making anyone go looking: how many do I have, what do they
 * buy, and where did they go. Organisations above the end customer also get a
 * fourth: how do I move them down the channel.
 *
 * Four independent queries feed it (balance, price list, ledger, channel), and
 * each fails on its own. The rule throughout: a query that failed is shown as a
 * failure with a retry, never as zero, free, empty or "no charges".
 *
 * Every action is gated on capability with `allows()`. That is a usability
 * affordance — the server re-checks each one — but a user who cannot allocate
 * credits must not be shown a control that looks like they can.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import { Card, CardHeader, StatTile, Skeleton, ErrorNotice, EmptyState } from '../components/ui';
import { IconCredits } from '../components/icons';
import { CreditLedger } from '../components/CreditLedger';
import { CreditAllocation } from '../components/CreditAllocation';
import {
  creditKeys, useCreditsT, type ChannelResponse, type CreditsKey,
} from '../components/creditsShared';

interface Pricing {
  services: Record<string, number>;
  frameworks: Array<{ key: string; name: string; licenseCost: number }>;
}

/** The order the price list reads in; anything the server adds follows it. */
const SERVICE_ORDER = [
  'TPRM_ASSESSMENT', 'VULNERABILITY_SCAN', 'SELF_ASSESSMENT_SCAN', 'MARITIME_ASSESSMENT',
];
const KNOWN_UNITS = new Set(['TPRM_ASSESSMENT', 'VULNERABILITY_SCAN']);

export function CreditsPage() {
  const { t, tx, tc, reason, num, credits } = useCreditsT();
  const { allows, session } = useAuth();
  const describeError = useApiError();

  const keys = creditKeys(session?.organization.id ?? '');
  const canRead = allows('credit.read');
  const canAllocate = allows('credit.adjust');

  const balance = useQuery({
    queryKey: keys.balance,
    queryFn: () => api.get<{ balance: number }>('/credits/balance'),
    enabled: canRead,
  });
  const pricing = useQuery({
    queryKey: keys.pricing,
    queryFn: () => api.get<Pricing>('/credits/pricing'),
    enabled: canRead,
    staleTime: 5 * 60_000,
  });
  // Wanted for two things: the allocation list, and naming the other side of a
  // transfer in the ledger. Administrative reach — no consent involved.
  const channel = useQuery({
    queryKey: keys.channel,
    queryFn: () => api.get<ChannelResponse>('/organizations'),
    enabled: allows('org.read'),
  });

  // After a refetch failure TanStack keeps the old value; a balance that may be
  // stale must not be presented as current, so an error wins over cached data.
  const balanceValue = balance.isError ? undefined : balance.data?.balance;
  const pricingData = pricing.isError ? undefined : pricing.data;

  if (!canRead) {
    return (
      <div className="oc-page animate-fade-in">
        <Card>
          <EmptyState icon={<IconCredits className="w-10 h-10" />}
                      title={tx('credits.title')}
                      description={t('error.INSUFFICIENT_PERMISSIONS', { defaultValue: 'You do not have permission to do that.' })} />
        </Card>
      </div>
    );
  }

  const cheapest = pricingData
    ? Math.min(...Object.values(pricingData.services), ...pricingData.frameworks.map((f) => f.licenseCost))
    : undefined;
  // The platform operator mints credits and spends none, so a zero balance is
  // its normal state and not a warning.
  const cannotAffordAnything =
    session?.organization.tier !== 'SUPERADMIN' &&
    balanceValue !== undefined && cheapest !== undefined && Number.isFinite(cheapest) && balanceValue < cheapest;

  const serviceKeys = pricingData
    ? [
        ...SERVICE_ORDER.filter((k) => k in pricingData.services),
        ...Object.keys(pricingData.services).filter((k) => !SERVICE_ORDER.includes(k)),
      ]
    : [];

  const affordable = (price: number) =>
    balanceValue !== undefined && price > 0 ? Math.floor(balanceValue / price) : null;

  const priceRow = (id: string, name: string, price: number, unit?: string) => {
    const n = affordable(price);
    return (
      <tr key={id} className="border-b border-line/60 align-top">
        <th scope="row" className="py-2.5 pr-3 text-left font-medium text-ink break-words">
          {name}
          {unit && <span className="block text-xs font-normal text-ink-muted">{unit}</span>}
        </th>
        <td className="py-2.5 pr-3 text-right tabular-nums whitespace-nowrap">{credits(price)}</td>
        <td className="py-2.5 text-right tabular-nums whitespace-nowrap text-ink-secondary">
          {n === null ? '—' : tx('credits.pricing.times', { count: n, formatted: num(n) })}
        </td>
      </tr>
    );
  };

  return (
    <div className="oc-page space-y-5 animate-fade-in">
      <header>
        <h1 className="oc-h1">{tx('credits.title')}</h1>
        <p className="oc-muted mt-1">{tx('credits.subtitle')}</p>
      </header>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        {/* Balance */}
        <div>
          {balance.isPending ? (
            <Skeleton className="h-28" />
          ) : balance.isError ? (
            <ErrorNotice message={`${tx('credits.balance.loadFailed')} ${describeError(balance.error)}`}
                         onRetry={() => void balance.refetch()} retryLabel={tc('common.retry')} />
          ) : (
            <StatTile
              label={tx('credits.balance.label')}
              icon={<IconCredits className="w-4 h-4" />}
              value={num(balance.data.balance)}
              tone={cannotAffordAnything ? 'serious' : undefined}
              hint={cannotAffordAnything
                ? tx('credits.balance.low')
                : session ? tx('credits.balance.heldBy', { organization: session.organization.name }) : undefined}
            />
          )}
        </div>

        {/* What credits buy */}
        <Card>
          <CardHeader title={tx('credits.pricing.title')} subtitle={tx('credits.pricing.subtitle')} />
          {pricing.isPending ? (
            <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-9" />)}</div>
          ) : pricing.isError ? (
            <ErrorNotice message={`${tx('credits.pricing.loadFailed')} ${describeError(pricing.error)}`}
                         onRetry={() => void pricing.refetch()} retryLabel={tc('common.retry')} />
          ) : serviceKeys.length === 0 && (pricingData?.frameworks.length ?? 0) === 0 ? (
            <p className="oc-muted">{tx('credits.pricing.none')}</p>
          ) : (
            <div className="space-y-3">
              {/* One table, so cost and "you can afford" line up down the whole list. */}
              <table className="w-full text-sm">
                <caption className="sr-only">{tx('credits.pricing.title')}</caption>
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-ink-muted border-b border-line">
                    <th scope="col" className="font-medium py-2 pr-3">{tx('credits.pricing.action')}</th>
                    <th scope="col" className="font-medium py-2 pr-3 text-right">{tx('credits.pricing.cost')}</th>
                    <th scope="col" className="font-medium py-2 text-right">{tx('credits.pricing.affordable')}</th>
                  </tr>
                </thead>
                <tbody>
                  {serviceKeys.map((key) => priceRow(
                    key, reason(key), pricingData!.services[key]!,
                    KNOWN_UNITS.has(key) ? tx(`credits.unit.${key}` as CreditsKey) : undefined,
                  ))}
                </tbody>
                {(pricingData?.frameworks.length ?? 0) > 0 && (
                  <tbody>
                    <tr>
                      <th scope="colgroup" colSpan={3} className="pt-4 pb-1 text-left">
                        <span className="block text-sm font-semibold text-ink">{tx('credits.pricing.frameworks')}</span>
                        <span className="block text-xs font-normal text-ink-muted">{tx('credits.pricing.frameworkUnit')}</span>
                      </th>
                    </tr>
                    {pricingData!.frameworks.map((f) => priceRow(f.key, f.name, f.licenseCost))}
                  </tbody>
                )}
              </table>

              <p className="text-xs text-ink-muted">{tx('credits.pricing.footnote')}</p>
            </div>
          )}
        </Card>
      </div>

      {/* Only those who may allocate see anything about it. */}
      {canAllocate && <CreditAllocation balance={balanceValue} channel={channel} />}

      <CreditLedger balance={balanceValue} channel={channel.data?.items} />
    </div>
  );
}
